import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useHcbbAdvancedFilters,
  useHcbbDetailNavigation,
  useHcbbProcedureDetails,
  useHcbbProfessionals,
  useHcbbResultView,
} from "@/hooks/use-hcbb-cost-estimator";
import {
  FACILITY_PAGE_SIZE,
  applyHcbbFacilityFilters,
  applyHcbbProfessionalFilters,
  countAppliedHcbbFilters,
  emptyHcbbFilterValueFor,
  hasRelevantHcbbFilters,
  resolveHcbbActiveTab,
  toHcbbResultKey,
} from "@/lib/hcbb-cost-estimator-utils";
import type { HcbbCostSearch } from "@/lib/hcbb-cost-estimator-utils";
import { INITIAL_HCBB_RESULT_VIEW } from "@/store/hcbb-cost-estimator/hcbb-result-view-slice";
import type { HcbbResultTab } from "@/store/hcbb-cost-estimator/hcbb-result-view-slice";
import type { HcbbAdvancedFilters } from "@/store/hcbb-cost-estimator/hcbb-advanced-filters-slice";
import { NoDataAvailable, SomethingWentWrong } from "@/pages/components/empty";
import { HcbbPriceHighlightCard } from "@/pages/components/hcbb-cost-estimator/hcbb-price-highlight";
import {
  HcbbFacilityCard,
  HcbbProviderCard,
} from "@/pages/components/hcbb-cost-estimator/hcbb-result-card";
import { HcbbActiveFilters } from "@/pages/components/hcbb-cost-estimator/hcbb-active-filters";
import {
  HcbbResultRowSkeleton,
  HcbbSearchResultsSkeleton,
} from "@/pages/components/hcbb-cost-estimator/hcbb-search-results-skeleton";
import MapView from "@/pages/components/provider-search/mapview";
import CostEstimatorDisclaimer from "@/pages/cost-estimator-disclaimer";
import RightSidebar from "@/pages/components/right-sidebar";
import useUserInfo from "@/hooks/use-user-info";
import SpendSummary from "@/pages/spend-summary";
import type {
  HcbbFacilityResult,
  HcbbPriceHighlight,
  HcbbProfessionalResult,
} from "@/model/hcbb-cost-estimator";
import type { PlaceMarker } from "@/model/place";

/**
 * Cost results for a committed search: the price benchmark beside the
 * facilities and providers that priced it.
 *
 * Every state the endpoint can land in is handled here, so the cards below only
 * ever render a payload that arrived intact.
 */
export const HcbbSearchResults = ({
  procedure,
  targetLocation,
  distance,
}: HcbbCostSearch) => {
  const { data, isPending, isFetching, isError, refetch } =
    useHcbbProcedureDetails({ procedure, targetLocation, distance });
  const [isSpendSummaryOpen, setIsSpendSummaryOpen] = useState(false);
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  // The marker doubles as the open flag — there is no map without one.
  const [mapMarker, setMapMarker] = useState<PlaceMarker | null>(null);
  const userInfo = useUserInfo();

  // Computed unconditionally, ahead of every early return below, so the
  // advanced-search panel's Redux state is always read for the search these
  // props actually describe — even while that search has nothing to show yet.
  const resultKey = toHcbbResultKey({ procedure, targetLocation, distance });
  const filters = useHcbbAdvancedFilters(resultKey);
  const view = useHcbbResultView(resultKey);

  if (!procedure || !targetLocation) {
    return (
      <NoDataAvailable
        title="No search to show"
        message="Pick a procedure and a location above to see estimated costs."
      />
    );
  }

  // `isPending` only. Every input to the search is in the query key, so
  // re-running it from the form on this page is a first load of a different
  // key rather than a refetch of this one. A background refetch here would
  // unmount the tabs below and discard every provider batch already loaded;
  // that case is reported by `aria-busy` instead.
  if (isPending) {
    return (
      <div aria-busy="true">
        {/* The skeleton itself is aria-hidden, so this is the only thing a
            screen reader hears while the estimates load. */}
        <p role="status" className="sr-only">
          Loading cost estimates for {procedure.description}.
        </p>
        <HcbbSearchResultsSkeleton />
      </div>
    );
  }

  // A failed request is recoverable — the estimate is worth a second attempt
  // before sending the member off to call support.
  if (isError || !data) {
    return (
      <div role="alert" className="flex flex-col items-center">
        <SomethingWentWrong message="We could not load cost estimates." />
        <Button variant="outline" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  // A partial payload is normal: HCBB can price a procedure with nothing in
  // network nearby, and can list providers it has no benchmark for. Either
  // half may also be missing outright, so neither is indexed into blind.
  const facilities = data.data?.facilities ?? [];
  // Full rows: the estimate carries name/specialty/network/tier/npi for
  // every candidate up front, though authoritative display/cost data still
  // comes from the providers tab's own batched detail fetch.
  const professionals = data.data?.professionals ?? [];
  const { highlight } = data;
  const hasProviders = facilities.length > 0 || professionals.length > 0;

  if (!highlight && !hasProviders) {
    return (
      <NoDataAvailable
        title="No cost estimates for this search"
        message={`We have no pricing or in-network providers for ${procedure.description} near ${targetLocation.description}. Try another procedure or a different location.`}
      />
    );
  }

  const openSpendSummary = () => setIsSpendSummaryOpen(true);
  const openDisclaimer = () => setIsDisclaimerOpen(true);

  // A new search is a new result set, so both lists go back to their first
  // page: `resultKey` (computed above) remounts the tabs via `key`, and the
  // stored view below is scoped to it so a stale depth can never be restored
  // onto different results.

  // The filtered professional candidate list's own reveal depth starts over
  // whenever the criteria themselves change — a stale depth from a previous
  // filter would either strand View More partway through the wrong set or
  // reveal more of the new one than the member ever asked to see.
  const removeAdvancedFilter = (key: keyof HcbbAdvancedFilters) => {
    filters.setFilters({
      ...filters.filters,
      [key]: emptyHcbbFilterValueFor(key),
    });
    view.setFilteredVisibleBatches(
      INITIAL_HCBB_RESULT_VIEW.filteredVisibleBatches,
    );
  };

  const hasAppliedFilters = countAppliedHcbbFilters(filters.filters) > 0;

  return (
    <>
      {/* The advanced-search panel itself is now embedded directly in
          HcbbSearchForm, a sibling of this component — both read the same
          Redux-backed `useHcbbAdvancedFilters(resultKey)`, so they always
          agree on open/closed state without any prop threading between them.
          The applied-filter chips stay independent of that panel and render
          here regardless of whether it's open. */}
      {hasAppliedFilters && (
        <HcbbActiveFilters
          filters={filters.filters}
          onRemove={removeAdvancedFilter}
          onClearAll={() => {
            filters.clearFilters();
            view.setFilteredVisibleBatches(
              INITIAL_HCBB_RESULT_VIEW.filteredVisibleBatches,
            );
          }}
        />
      )}

      <section
        aria-label="Cost estimate results"
        aria-busy={isFetching}
        className="flex gap-4"
      >
        {highlight && (
          <div className="w-1/4">
            <HcbbPriceHighlightCard highlight={highlight} />
          </div>
        )}

        <div className="flex-1">
          {hasProviders ? (
            <HcbbResultTabs
              key={resultKey}
              resultKey={resultKey}
              search={{ procedure, targetLocation, distance }}
              facilities={facilities}
              professionals={professionals}
              highlight={highlight}
              onViewOnMap={setMapMarker}
              onViewSpendSummary={openSpendSummary}
              onViewDisclaimer={openDisclaimer}
            />
          ) : (
            // Prices came back but nothing in network did: the highlight card
            // is still worth showing next to this.
            <NoDataAvailable
              title="No providers or facilities in range"
              message="No network facilities or providers matched this procedure within the distance you chose. Try a wider distance or a different location."
            />
          )}
        </div>
      </section>

      {/* Matches the classic estimator: the deductible "View" link opens the
          spend summary beside the results rather than navigating away. */}
      <RightSidebar
        title="Spend Summary"
        isOpen={isSpendSummaryOpen}
        onClose={() => setIsSpendSummaryOpen(false)}
        startBelowHeader
      >
        <Suspense fallback={<div className="p-4">Loading...</div>}>
          <SpendSummary shouldWrap={false} useMedCoverage />
        </Suspense>
      </RightSidebar>

      {/* Every row's estimate carries the same caveats, so one panel serves
          them all — the same disclaimer the classic estimator shows under
          its results, opened beside them rather than under a tab. */}
      <RightSidebar
        title="Disclaimer"
        isOpen={isDisclaimerOpen}
        onClose={() => setIsDisclaimerOpen(false)}
        startBelowHeader
      >
        {/* Mounted only while open: the panel keeps its children in the DOM
            when closed, and a document this long would sit off-screen with
            every link in it still reachable by Tab. */}
        {isDisclaimerOpen && (
          <CostEstimatorDisclaimer
            planName={userInfo.planId}
            serviceProvider={userInfo.serviceProvider}
            supportNumber={userInfo.supportNumber}
          />
        )}
      </RightSidebar>

      {/* One pin at a time — the row's own location, not the whole result set,
          which is what the Map button on a single card promises. */}
      <RightSidebar
        title="Map View"
        isOpen={Boolean(mapMarker)}
        onClose={() => setMapMarker(null)}
        startBelowHeader
        fillBody
      >
        {/* Mounted only while open: MapView builds the map in an effect and
            needs a container that already has a size.

            The canvas takes every pixel the address card underneath does not
            want — a 192px default map in a full-height panel wastes most of
            it. `maxZoom` because a single pin gives `fitBounds` a zero-area
            box, which otherwise zooms to a rooftop. */}
        {mapMarker && (
          <MapView
            searchResult={[mapMarker]}
            className="mb-2 min-h-0 flex-1"
            maxZoom={15}
          />
        )}
      </RightSidebar>
    </>
  );
};

/**
 * The two result tabs, and how much of each list is revealed.
 *
 * Both tabs' progress is read from the store rather than local state, for
 * two reasons. Radix unmounts the inactive tab's content, so state below this
 * point would snap back to page one on every toggle; and this component is
 * unmounted outright when the member opens a detail screen, so state here
 * would not survive the trip back either. The providers hook lives at this
 * level for the same reason, which also means its first batch is requested as
 * soon as the results render rather than on first open of the tab.
 */
const HcbbResultTabs = ({
  search,
  resultKey,
  facilities,
  professionals,
  highlight,
  onViewOnMap,
  onViewSpendSummary,
  onViewDisclaimer,
}: {
  search: HcbbCostSearch;
  resultKey: string;
  facilities: HcbbFacilityResult[];
  professionals: HcbbProfessionalResult[];
  highlight: HcbbPriceHighlight | null;
  onViewOnMap: (marker: PlaceMarker) => void;
  onViewSpendSummary: () => void;
  onViewDisclaimer: () => void;
}) => {
  const view = useHcbbResultView(resultKey);
  const filters = useHcbbAdvancedFilters(resultKey);
  const { openFacilityDetail, openProviderDetail } = useHcbbDetailNavigation();

  const activeTab = resolveHcbbActiveTab(view.activeTab, facilities);

  // Filtering runs against the full /estimate lists, before paging — both
  // lists carry everything needed to filter (name, specialty, network,
  // facility type, npi) without waiting on any batch fetch, so "View More"
  // and the tab count both describe the true filtered total, not just
  // whatever has been fetched so far.
  const filteredFacilities = useMemo(
    () => applyHcbbFacilityFilters(facilities, filters.filters),
    [facilities, filters.filters],
  );
  const filteredProfessionals = useMemo(
    () => applyHcbbProfessionalFilters(professionals, filters.filters),
    [professionals, filters.filters],
  );
  const hasFacilityFilters = hasRelevantHcbbFilters(
    filters.filters,
    "facilities",
  );
  const hasProfessionalFilters = hasRelevantHcbbFilters(
    filters.filters,
    "professionals",
  );

  // Two different candidate lists feed the batching/reveal machinery below,
  // never mixed: the full estimate list while unfiltered, the filtered
  // subset once a provider-relevant filter is applied — each with its own
  // reveal depth, so clearing a filter restores the unfiltered list's own
  // previously-revealed depth instead of resetting to page one.
  const professionalCandidates = hasProfessionalFilters
    ? filteredProfessionals
    : professionals;
  const professionalsResult = useHcbbProfessionals(
    search,
    professionalCandidates,
    highlight,
    hasProfessionalFilters
      ? {
          visibleBatches: view.filteredVisibleBatches,
          onVisibleBatchesChange: view.setFilteredVisibleBatches,
        }
      : {
          visibleBatches: view.visibleBatches,
          onVisibleBatchesChange: view.setVisibleBatches,
        },
  );

  return (
    <Tabs
      value={activeTab}
      onValueChange={(tab) => view.setActiveTab(tab as HcbbResultTab)}
    >
      <TabsList className="bg-gray-200 text-gray-800">
        <TabsTrigger
          value="facilities"
          className="data-[state=active]:text-primary"
        >
          {/* The filtered count once a facility-relevant filter is applied,
              so the tab reflects what the member would actually see there —
              the raw count otherwise, unchanged from before filtering existed. */}
          Facilities (
          {hasFacilityFilters ? filteredFacilities.length : facilities.length})
        </TabsTrigger>
        <TabsTrigger
          value="professionals"
          className="data-[state=active]:text-primary"
        >
          {/* Both counts are the true total, known instantly from the
              estimate — filtered or not, neither waits on a batch fetch. */}
          Providers (
          {hasProfessionalFilters
            ? filteredProfessionals.length
            : professionals.length}
          )
        </TabsTrigger>
      </TabsList>

      <TabsContent value="facilities">
        <ResultList
          items={filteredFacilities.slice(0, view.visibleFacilities)}
          totalCount={filteredFacilities.length}
          remaining={Math.max(
            filteredFacilities.length - view.visibleFacilities,
            0,
          )}
          itemLabel="facilities"
          onViewMore={() =>
            view.setVisibleFacilities(
              view.visibleFacilities + FACILITY_PAGE_SIZE,
            )
          }
          emptyMessage={
            hasFacilityFilters
              ? "No facilities match your advanced search filters. Try clearing them or choosing different criteria."
              : "No in-network facilities matched this procedure within the distance you chose. The Providers tab still has results."
          }
          renderItem={(facility) => (
            <HcbbFacilityCard
              facility={facility}
              onViewOnMap={onViewOnMap}
              onViewSpendSummary={onViewSpendSummary}
              onViewDisclaimer={onViewDisclaimer}
              onViewDetails={() => openFacilityDetail(facility, highlight)}
            />
          )}
        />
      </TabsContent>

      <TabsContent value="professionals">
        <ResultList
          items={professionalsResult.items}
          totalCount={professionalsResult.totalCount}
          remaining={professionalsResult.remaining}
          loadingCount={professionalsResult.loadingCount}
          loadMoreError={professionalsResult.loadMoreError}
          onRetry={professionalsResult.retry}
          itemLabel="providers"
          onViewMore={professionalsResult.viewMore}
          emptyMessage={
            hasProfessionalFilters
              ? "No providers match your advanced search filters. Try clearing them or choosing different criteria."
              : "No individual providers matched this procedure within the distance you chose. The Facilities tab still has results."
          }
          renderItem={(professional) => (
            <HcbbProviderCard
              professional={professional}
              onViewOnMap={onViewOnMap}
              onViewSpendSummary={onViewSpendSummary}
              onViewDisclaimer={onViewDisclaimer}
              onViewDetails={() => openProviderDetail(professional)}
            />
          )}
        />
      </TabsContent>
    </Tabs>
  );
};

/**
 * One tab's scrolling list, a page at a time — or why it has nothing to show.
 *
 * The caller slices, and passes what is still to come as `remaining`. That is
 * not the same as `totalCount - items.length` for the providers tab: a batch
 * can answer with fewer rows than references it was sent, and counting rows
 * would then strand the references never requested.
 */
const ResultList = <T extends { id: number }>({
  items,
  totalCount,
  remaining,
  itemLabel,
  onViewMore,
  emptyMessage,
  renderItem,
  loadingCount = 0,
  loadMoreError = null,
  onRetry,
}: {
  /** Already sliced by the caller. */
  items: T[];
  /** What the search found; may exceed `items` while batches are in flight. */
  totalCount: number;
  /** How many more View More would reveal. */
  remaining: number;
  /** Plural noun for the count line, e.g. "facilities". */
  itemLabel: string;
  onViewMore: () => void;
  emptyMessage: string;
  renderItem: (item: T) => ReactNode;
  /** Rows being fetched right now; 0 for a list already in memory. */
  loadingCount?: number;
  loadMoreError?: Error | null;
  onRetry?: () => void;
}) => {
  const countRef = useRef<HTMLParagraphElement>(null);
  const viewMoreRef = useRef<HTMLButtonElement>(null);
  const retryRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(false);

  const isLoading = loadingCount > 0;

  // The control the member just used can leave the DOM on the click (the last
  // page, already in memory) or a response later (the last batch, still
  // loading). Either way focus would fall to the body, so it is parked on the
  // count — the one thing that changed.
  useEffect(() => {
    if (!restoreFocusRef.current) return;
    if (viewMoreRef.current || retryRef.current) return;

    restoreFocusRef.current = false;
    countRef.current?.focus();
  }, [remaining, loadingCount, loadMoreError]);

  // Nothing came back and nothing is on its way. A batch still loading, or one
  // that failed, has something better to say than "no results".
  if (items.length === 0 && !isLoading && !loadMoreError) {
    return (
      <div className="border-1 border-gray rounded-lg border p-6 text-center text-sm text-gray-600 shadow-md">
        {emptyMessage}
      </div>
    );
  }

  const handleViewMore = () => {
    if (isLoading) return;
    restoreFocusRef.current = true;
    onViewMore();
  };

  const handleRetry = () => {
    restoreFocusRef.current = true;
    onRetry?.();
  };

  return (
    <>
      <ul
        aria-busy={isLoading}
        className="h-[calc(100vh-215px)] space-y-2 overflow-y-auto"
      >
        {items.map((item) => (
          <li key={item.id}>{renderItem(item)}</li>
        ))}

        {/* Appended rather than substituted, so the rows above stay put while
            these fill in. The count line is what announces them. */}
        {Array.from({ length: loadingCount }, (_, index) => (
          <li key={`loading-${index}`} aria-hidden="true">
            <HcbbResultRowSkeleton />
          </li>
        ))}

        {loadMoreError && onRetry && (
          <li>
            <div
              role="alert"
              className="border-1 rounded-lg border border-red-200 p-4 text-center text-sm text-red-600 shadow-md"
            >
              <p>We could not load the next {itemLabel}.</p>
              <Button
                ref={retryRef}
                variant="outline"
                className="mt-2"
                onClick={handleRetry}
                aria-label={`Retry loading the next ${itemLabel}`}
              >
                Try again
              </Button>
            </div>
          </li>
        )}

        {/* Inside the scroll box, so it reads as the end of the list rather
            than a control that acts on the whole tab. */}
        {remaining > 0 && (
          <li className="pb-2 text-center">
            {/* `aria-disabled`, not `disabled`: a disabled button that has
                focus stops being announced and can drop focus to the body. */}
            <Button
              ref={viewMoreRef}
              variant="link"
              aria-disabled={isLoading || undefined}
              onClick={handleViewMore}
            >
              {isLoading
                ? `Loading more ${itemLabel}…`
                : `View More (${remaining} more)`}
            </Button>
          </li>
        )}
      </ul>

      {/* Outside the scroll box so the total is readable without scrolling to
          it, and the one region that announces every append. */}
      <p
        ref={countRef}
        tabIndex={-1}
        role="status"
        className="pt-2 text-center text-xs text-gray-600 focus-visible:outline-none"
      >
        {isLoading
          ? `Showing ${items.length} of ${totalCount} ${itemLabel}. Loading ${loadingCount} more.`
          : remaining === 0 && items.length < totalCount
            ? `Showing all ${items.length} available ${itemLabel}.`
            : `Showing ${items.length} of ${totalCount} ${itemLabel}.`}
      </p>
    </>
  );
};
